import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useBreakpointUp } from '@travel-frontend/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { List } from '../../../components/Image/GalleryOverlay/components/List.js';
import { Tile } from '../../../components/Image/GalleryOverlay/components/Tile.js';
import { Image } from '../../../components/Image/Image.js';
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
export const GalleryOverlay = ({
  images,
  activeImage = 0,
  onSelectImage
}) => {
  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');
  const columns = lgUp ? 4 : mdUp ? 3 : 2;
  const refs = useRef([]);
  const containerRef = useRef(null);
  const [focusedImageIndex, setFocusedImageIndex] = useState(null);
  const [isGridFocused, setIsGridFocused] = useState(false);
  useEffect(() => {
    refs.current = Array.from({
      length: images.length
    }).fill(null);
  }, [images.length]);
  const focusImage = useCallback(imageIndex => {
    const element = refs.current[imageIndex];
    if (element) {
      element.focus();
      setFocusedImageIndex(imageIndex);
    }
  }, []);
  const moveFocus = useCallback(direction => {
    if (focusedImageIndex === null) return;
    let newIndex = focusedImageIndex;
    switch (direction) {
      case 'left':
        {
          newIndex = clamp(focusedImageIndex - 1, 0, images.length - 1);
          break;
        }
      case 'right':
        {
          newIndex = clamp(focusedImageIndex + 1, 0, images.length - 1);
          break;
        }
      case 'up':
        {
          newIndex = clamp(focusedImageIndex - columns, 0, images.length - 1);
          break;
        }
      case 'down':
        {
          newIndex = clamp(focusedImageIndex + columns, 0, images.length - 1);
          break;
        }
    }
    if (newIndex !== focusedImageIndex) {
      setFocusedImageIndex(newIndex);
      focusImage(newIndex);
    }
  }, [focusedImageIndex, images.length, columns, focusImage]);
  const handleKeyDown = useCallback(event => {
    if (!isGridFocused) return;
    switch (event.key) {
      case 'ArrowLeft':
        {
          event.preventDefault();
          moveFocus('left');
          break;
        }
      case 'ArrowRight':
        {
          event.preventDefault();
          moveFocus('right');
          break;
        }
      case 'ArrowUp':
        {
          event.preventDefault();
          moveFocus('up');
          break;
        }
      case 'ArrowDown':
        {
          event.preventDefault();
          moveFocus('down');
          break;
        }
      case 'Enter':
      case ' ':
        {
          event.preventDefault();
          if (focusedImageIndex !== null) {
            onSelectImage(focusedImageIndex);
          }
          break;
        }
      case 'Escape':
        {
          event.preventDefault();
          setIsGridFocused(false);
          setFocusedImageIndex(null);
          containerRef.current?.blur();
          break;
        }
    }
  }, [isGridFocused, moveFocus, focusedImageIndex, onSelectImage]);
  useEffect(() => {
    if (!isGridFocused) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isGridFocused, handleKeyDown]);
  useEffect(() => {
    if (!activeImage || !refs?.current) {
      return;
    }
    const element = refs.current[activeImage];
    if (element) {
      element.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [activeImage]);
  useEffect(() => {
    if (activeImage && activeImage > images.length - 1) {
      onSelectImage(0);
    }
  }, [activeImage, images.length, onSelectImage]);
  const handleContainerFocus = useCallback(() => {
    setIsGridFocused(true);
    if (focusedImageIndex === null) {
      focusImage(activeImage);
    }
  }, [activeImage, focusedImageIndex, focusImage]);
  const handleContainerBlur = useCallback(event => {
    if (!containerRef.current?.contains(event.relatedTarget)) {
      setIsGridFocused(false);
      setFocusedImageIndex(null);
    }
  }, []);
  const handleImageFocus = useCallback(index => {
    setFocusedImageIndex(index);
    setIsGridFocused(true);
  }, []);
  return _jsx(List, {
    columns: columns,
    ref: containerRef,
    role: "grid",
    "aria-label": "Photo gallery",
    tabIndex: isGridFocused ? -1 : 0,
    onFocus: handleContainerFocus,
    onBlur: handleContainerBlur,
    children: images.map((image, index) => {
      const isFocused = focusedImageIndex === index && isGridFocused;
      return _jsx(Tile, {
        ref: element => {
          if (element) {
            refs.current[index] = element;
          }
        },
        active: index === activeImage,
        tabIndex: index === activeImage && !isGridFocused ? 0 : -1,
        role: "gridcell",
        "aria-label": `Photo ${index + 1} of ${images.length}`,
        onClick: () => onSelectImage(index),
        onFocus: () => handleImageFocus(index),
        style: {
          outline: isFocused ? '2px dashed #ff39dc' : 'none',
          outlineOffset: '2px'
        },
        children: _jsx(Image, {
          src: image
        })
      }, image);
    })
  });
};